import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, RotateCcw, RotateCw } from "lucide-react";

const VIDEO_ID = "O11WSb8Oqko";

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const loadYouTubeAPI = (): Promise<any> => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  return new Promise((resolve) => {
    const existing = document.getElementById("yt-iframe-api");
    if (!existing) {
      const tag = document.createElement("script");
      tag.id = "yt-iframe-api";
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
    const poll = setInterval(() => {
      if (window.YT && window.YT.Player) {
        clearInterval(poll);
        resolve(window.YT);
      }
    }, 200);
  });
};

const formatTime = (s: number) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const HeroVideoPlayer = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    let destroyed = false;
    let interval: ReturnType<typeof setInterval>;

    loadYouTubeAPI().then((YT) => {
      if (destroyed || !containerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        videoId: VIDEO_ID,
        host: "https://www.youtube-nocookie.com",
        playerVars: {
          controls: 0,
          modestbranding: 1,
          rel: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          playsinline: 1,
        },
        events: {
          onReady: (e: any) => {
            setReady(true);
            setDuration(e.target.getDuration() || 0);
            setMuted(e.target.isMuted());
          },
          onStateChange: (e: any) => {
            setPlaying(e.data === 1);
            setDuration(e.target.getDuration() || 0);
          },
        },
      });

      interval = setInterval(() => {
        const p = playerRef.current;
        if (p?.getCurrentTime) {
          setCurrent(p.getCurrentTime() || 0);
          const d = p.getDuration?.() || 0;
          if (d) setDuration(d);
        }
      }, 250);
    });

    return () => {
      destroyed = true;
      clearInterval(interval);
      playerRef.current?.destroy?.();
    };
  }, []);

  const togglePlay = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    playing ? p.pauseVideo() : p.playVideo();
  }, [playing]);

  const toggleMute = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    if (p.isMuted()) {
      p.unMute();
      setMuted(false);
    } else {
      p.mute();
      setMuted(true);
    }
  }, []);

  const seekBy = (delta: number) => {
    const p = playerRef.current;
    if (!p) return;
    p.seekTo(Math.max(0, Math.min((p.getCurrentTime() || 0) + delta, duration)), true);
  };

  const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const p = playerRef.current;
    if (!p) return;
    const t = (Number(e.target.value) / 100) * duration;
    p.seekTo(t, true);
    setCurrent(t);
  };

  const progress = duration ? (current / duration) * 100 : 0;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-[#3b82f6]/25 shadow-2xl bg-black">
      <div className="relative w-full aspect-video">
        <div ref={containerRef} className="absolute inset-0 w-full h-full pointer-events-none [&>iframe]:w-full [&>iframe]:h-full" />
        {/* Capa que bloquea toda interacción con YouTube */}
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pausar video" : "Reproducir video"}
          className="absolute inset-0 w-full h-full cursor-pointer bg-transparent"
        />
        {!playing && ready && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 rounded-full bg-[#2563eb]/90 flex items-center justify-center shadow-lg">
              <Play className="w-7 h-7 text-white ml-1" />
            </div>
          </div>
        )}
      </div>

      {/* Controles propios */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[#0a1628] border-t border-white/10">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pausar" : "Reproducir"}
          className="text-white/85 hover:text-white transition-colors"
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>
        <button type="button" onClick={() => seekBy(-10)} aria-label="Retroceder 10 segundos" className="text-white/70 hover:text-white transition-colors">
          <RotateCcw className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => seekBy(10)} aria-label="Adelantar 10 segundos" className="text-white/70 hover:text-white transition-colors">
          <RotateCw className="w-4 h-4" />
        </button>

        <span className="text-[11px] tabular-nums text-white/60 w-10 text-right">{formatTime(current)}</span>

        <input
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={progress}
          onChange={onScrub}
          aria-label="Progreso del video"
          className="flex-1 h-1 appearance-none rounded-full cursor-pointer bg-white/15 accent-[#60a5fa]"
          style={{ background: `linear-gradient(to right, #60a5fa ${progress}%, rgba(255,255,255,0.15) ${progress}%)` }}
        />

        <span className="text-[11px] tabular-nums text-white/60 w-10">{formatTime(duration)}</span>

        <button type="button" onClick={toggleMute} aria-label={muted ? "Activar sonido" : "Silenciar"} className="text-white/70 hover:text-white transition-colors">
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

export default HeroVideoPlayer;
